/**
 * VP-175 — własna ścieżka uruchamiania reklamy z poziomu wizarda.
 *
 * Wizard NIE przerzuca usera do CampaignBuildera. Buduje minimalny, bezpieczny
 * payload (cel: ruch na stronę — nie wymaga piksela ani formularza leadowego,
 * więc zawsze da się uruchomić) i strzela w istniejący endpoint
 * POST /api/campaign-creator/launch (SSE) — reużywamy całą logikę launchu Meta.
 */
import { apiRequest, uploadFile, getAuthToken } from "@/lib/queryClient";
import type { WizardCreative } from "./launch-body";

export { buildWizardLaunchBody, type WizardAdInput, type WizardCreative } from "./launch-body";

export type LaunchStepEvent = {
  step: string;
  status: "pending" | "done" | "error";
  message?: string;
};

export type WizardLaunchResult = {
  success?: boolean;
  campaignId?: string;
  adsManagerUrl?: string;
  message?: string;
};

/**
 * Konsumuje SSE z POST /launch (wzorzec z CampaignBuilder.readSSELaunch) i raportuje
 * postęp kroków przez onStep. Zwraca finalny wynik (event `done`).
 */
export async function launchAdOverSSE(
  body: Record<string, unknown>,
  onStep: (event: LaunchStepEvent) => void
): Promise<WizardLaunchResult> {
  const token = getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch("/api/campaign-creator/launch", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("text/event-stream")) {
    const text = await response.text();
    if (!response.ok) {
      let message = text;
      try {
        message = (JSON.parse(text) as { message?: string }).message || text;
      } catch {
        /* nie-JSON — zostaw surowy tekst */
      }
      throw new Error(message);
    }
    return JSON.parse(text) as WizardLaunchResult;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult: WizardLaunchResult | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";

    for (const part of parts) {
      const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
      if (!dataLine) continue;
      const data = JSON.parse(dataLine.slice(6)) as
        | ({ type: "step" } & LaunchStepEvent)
        | ({ type: "done" } & WizardLaunchResult)
        | { type: "error"; message?: string };
      if (data.type === "done") {
        finalResult = data;
      } else if (data.type === "step") {
        onStep({ step: data.step, status: data.status, message: data.message });
      } else if (data.type === "error") {
        throw new Error(data.message || "Uruchomienie reklamy nie powiodło się.");
      }
    }
  }

  if (!finalResult) throw new Error("Połączenie przerwane w trakcie uruchamiania reklamy.");
  return finalResult;
}

/**
 * Upload pliku graficznego/wideo do S3 (presign → PUT, fallback: proxy upload).
 * Ten sam wzorzec co FastTrackFlow — ZERO base64, od razu URL z S3.
 */
export async function uploadCreativeFile(file: File): Promise<WizardCreative> {
  const isVideo = file.type.startsWith("video/");
  const mimeType = file.type || (isVideo ? "video/mp4" : "image/jpeg");
  const { uploadUrl, getUrl } = (await apiRequest("POST", "/api/campaign-creator/r2-presign", {
    filename: file.name,
    mimeType,
  })) as { uploadUrl: string; getUrl: string; key: string };

  let finalUrl = "";
  try {
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": mimeType },
    });
    if (putRes.ok) finalUrl = getUrl;
  } catch {
    /* presigned PUT nieudany — fallback niżej */
  }
  if (!finalUrl) {
    const formData = new FormData();
    formData.append("file", file);
    const result = (await uploadFile("/api/campaign-creator/r2-upload", formData)) as {
      url: string;
    };
    finalUrl = result.url;
  }
  return { url: finalUrl, type: isVideo ? "video" : "image", name: file.name };
}
